import React from "react";
import { TERMS_LAST_UPDATED, TERMS_VERSION } from "../lib/legal";

const sections = [
  {
    title: "1. Acceptance of terms",
    clauses: [
      {
        heading: "1.1",
        text: "By accessing or using InvoiceU you confirm that:",
        bullets: [
          "you are at least 18 years old;",
          "you have the authority to enter into these Terms on behalf of yourself or your organisation; and",
          "you agree to be bound by these Terms and any additional policies or guidelines we publish within the Service from time to time.",
        ],
      },
      {
        heading: "1.2",
        text: 'If you are using the Service on behalf of a company or other legal entity, "you" refers to that entity, and you represent that you have authority to bind that entity to these Terms.',
      },
      {
        heading: "1.3",
        text: "If you do not meet these requirements, you must not use the Service.",
      },
    ],
  },
  {
    title: "2. Your account",
    clauses: [
      {
        heading: "2.1",
        text: "You are responsible for:",
        bullets: [
          "maintaining the confidentiality and security of your login credentials; and",
          "all activities that occur under your account, whether authorised by you or not.",
        ],
      },
      {
        heading: "2.2",
        text: "You must notify us promptly at support@invoiceu.co.uk if:",
        bullets: [
          "you suspect any unauthorised access to your account; or",
          "you become aware of any breach of security relating to the Service.",
        ],
      },
      {
        heading: "2.3",
        text: "We are not liable for any loss, damage, or other consequences arising from unauthorised access to your account where we have not caused such access.",
      },
      {
        heading: "2.4",
        text: "We may suspend or restrict access to your account (with or without notice) if we reasonably believe that:",
        bullets: [
          "your account has been compromised;",
          "you are misusing the Service;",
          "you are in breach of these Terms; or",
          "such action is necessary to protect the Service, our systems, or other users.",
        ],
      },
    ],
  },
  {
    title: "3. Usage rules",
    clauses: [
      {
        heading: "3.1",
        text: "You agree that you will not:",
        bullets: [
          "upload, store, or transmit any content that is unlawful, infringing, defamatory, harassing, abusive, fraudulent, or otherwise objectionable;",
          "attempt to gain unauthorised access to the Service or related systems;",
          "probe, scan, or test the vulnerability of any system or network;",
          "copy, modify, adapt, decompile, reverse engineer, or attempt to derive the source code of the Service, except to the extent such restriction is prohibited by law;",
          "interfere with or disrupt the integrity, performance, or security of the Service;",
          "use the Service for any purpose that violates applicable law or regulations.",
        ],
      },
      {
        heading: "3.2",
        text: "You must respect any fair-use or usage limits set out in your plan. Unless explicitly allowed in your plan:",
        bullets: [
          "you must not use automated tools to scrape, export, or download data from the Service in bulk; and",
          "you must not attempt to bypass technical measures that enforce plan limits.",
        ],
      },
      {
        heading: "3.3",
        text: "We may monitor usage to ensure compliance with these Terms and fair-use limits. We are not obligated to monitor all content or activity but may do so at our discretion.",
      },
    ],
  },
  {
    title: "4. Billing and subscriptions",
    clauses: [
      {
        heading: "4.1",
        text: 'Certain features of the Service are available on a paid subscription basis ("Paid Plans"). By subscribing to a Paid Plan, you authorise us (or our payment processor) to charge you the fees displayed at checkout, plus any applicable taxes, at the billing interval you select.',
      },
      {
        heading: "4.2",
        text: "Automatic renewal. Paid Plans renew automatically at the end of each billing period unless you cancel before the renewal date.",
      },
      {
        heading: "4.3",
        text: "Non-refundable fees. All fees are non-refundable, except where required by law or where we explicitly state otherwise in writing.",
      },
      {
        heading: "4.4",
        text: "Upgrades and downgrades.",
        bullets: [
          "Upgrades take effect immediately. Any additional fees will be charged on a pro-rated basis for the remainder of the billing period, or as otherwise specified at checkout.",
          "Downgrades or cancellations generally take effect at the end of your current billing period, unless we state otherwise. You remain responsible for any fees due for the remainder of that period.",
        ],
      },
      {
        heading: "4.5",
        text: "We may change the pricing of Paid Plans from time to time. We will provide notice of any material price changes, and such changes will take effect from the next billing cycle unless you cancel before that date.",
      },
      {
        heading: "4.6",
        text: "We are not responsible for any bank charges, currency conversion fees, or other charges imposed by your payment provider.",
      },
    ],
  },
  {
    title: "5. Tap to Pay (Stripe)",
    clauses: [
      {
        heading: "5.1",
        text: "Tap to Pay lets you accept in-person card payments using Stripe. To use Tap to Pay, you must connect a Stripe account and agree to Stripe's terms. Stripe is the payment processor and provides the payment services.",
      },
      {
        heading: "5.2",
        text: "You authorise us to share information about you, your business, and your transactions with Stripe for onboarding, compliance, and payment processing. Stripe may require additional verification or impose limits, and Tap to Pay may be suspended if those requirements are not met.",
      },
      {
        heading: "5.3",
        text: "Payouts. Stripe pays card proceeds to your nominated bank account. The first payout is subject to an initial payout period that may be longer than subsequent payouts while Stripe completes onboarding and risk review. After the initial payout period, payouts follow the standard schedule set by Stripe for your account (for example, daily or weekly), and timing can vary by region, bank processing times, or compliance checks.",
      },
      {
        heading: "5.4",
        text: "Fees, refunds, and disputes.",
        bullets: [
          "you are responsible for all Stripe fees, chargebacks, refunds, and disputes related to Tap to Pay transactions;",
          "we may pass through or deduct any amounts owed to Stripe or card networks; and",
          "we are not responsible for Stripe's actions, decisions, or payout timing.",
        ],
      },
    ],
  },
  {
    title: "6. Data and privacy",
    clauses: [
      {
        heading: "6.1",
        text: 'Your ownership. You retain all rights, title, and interest in and to the data you upload or create in the Service ("Your Data").',
      },
      {
        heading: "6.2",
        text: "Licence to us. You grant us a worldwide, non-exclusive, royalty-free licence to host, store, process, transmit, back up, and otherwise use Your Data as reasonably necessary to:",
        bullets: [
          "provide, maintain, and operate the Service;",
          "improve and develop the Service; and",
          "comply with legal obligations.",
        ],
      },
      {
        heading: "6.3",
        text: "Security. We implement reasonable technical and organisational measures to protect Your Data. However, no system is perfectly secure, and we cannot guarantee absolute security.",
      },
      {
        heading: "6.4",
        text: "Your responsibilities. You are responsible for:",
        bullets: [
          "using strong passwords and enabling any additional security features we provide;",
          "maintaining your own independent backups of Your Data; and",
          "ensuring that Your Data and your use of the Service comply with all applicable laws and regulations.",
        ],
      },
      {
        heading: "6.5",
        text: "Data loss. To the maximum extent permitted by law, we are not liable for:",
        bullets: [
          "any loss, corruption, or deletion of Your Data;",
          "any failure to store or back up Your Data; or",
          "any costs or damages arising from such loss or failure.",
        ],
        note: "You should not rely on the Service as your sole repository for important data.",
      },
      {
        heading: "6.6",
        text: "Account deletion. If you delete your account, or if we terminate it in accordance with these Terms:",
        bullets: [
          "your access to the Service and Your Data may cease immediately;",
          "we may delete or anonymise Your Data from our active systems;",
          "we may retain limited information as needed for legal compliance, billing, audit, and dispute resolution purposes.",
        ],
      },
    ],
  },
  {
    title: "7. Invoices and outputs",
    clauses: [
      {
        heading: "7.1",
        text: "The Service provides tools to help you create and manage invoices and related outputs. We do not provide legal, tax, or accounting advice.",
      },
      {
        heading: "7.2",
        text: "You are solely responsible for:",
        bullets: [
          "the accuracy, completeness, and legality of all content you enter into the Service, including invoice details and recipient information;",
          "ensuring that your invoices and other outputs comply with applicable tax, accounting, and legal requirements in your jurisdiction;",
          "verifying tax rates, tax treatment, and any other regulatory requirements relevant to your business.",
        ],
      },
      {
        heading: "7.3",
        text: "We do not warrant that:",
        bullets: [
          "invoices generated or stored through the Service will meet any specific legal or regulatory standard; or",
          "any calculations performed by the Service (for example, totals or tax amounts) are accurate for your specific use case.",
        ],
      },
      {
        heading: "7.4",
        text: "To the fullest extent permitted by law, we are not responsible for:",
        bullets: [
          "fines, penalties, tax liabilities, lost revenue, or other losses arising from your use of the Service, including any errors in invoices or outputs; or",
          "any reliance you place on information or calculations generated by the Service.",
        ],
      },
    ],
  },
  {
    title: "8. Intellectual property",
    clauses: [
      {
        heading: "8.1",
        text: 'All software, code, interfaces, designs, logos, branding, and other content provided as part of the Service ("InvoiceU Content") are owned by us or our licensors and are protected by intellectual property laws.',
      },
      {
        heading: "8.2",
        text: "Subject to your compliance with these Terms and payment of any applicable fees, we grant you a limited, non-exclusive, non-transferable, revocable licence to access and use the Service for your internal business purposes.",
      },
      {
        heading: "8.3",
        text: "You must not:",
        bullets: [
          "copy, modify, adapt, or create derivative works based on the Service or InvoiceU Content;",
          "distribute, sell, resell, lease, or sublicense the Service to any third party;",
          "remove, alter, or obscure any proprietary notices or branding.",
        ],
      },
      {
        heading: "8.4",
        text: "You agree that we may use your organisation's name and logo in our marketing materials and customer lists, unless you request otherwise in writing.",
      },
    ],
  },
  {
    title: "9. Termination",
    clauses: [
      {
        heading: "9.1",
        text: "You may stop using the Service at any time. For Paid Plans, cancellation takes effect according to Section 4.",
      },
      {
        heading: "9.2",
        text: "We may suspend or terminate your access to the Service (in whole or in part) at any time if:",
        bullets: [
          "you violate these Terms or applicable law;",
          "your use of the Service poses a security risk or could adversely impact the Service or other users;",
          "we are required to do so by law or by a third-party service provider; or",
          "we decide to discontinue the Service (in which case we will provide reasonable notice where practicable).",
        ],
      },
      {
        heading: "9.3",
        text: "Upon termination or suspension:",
        bullets: [
          "your right to access and use the Service will cease;",
          "we may delete or restrict access to Your Data, subject to Section 6.6; and",
          "any fees already paid are non-refundable, except where required by law.",
        ],
      },
      {
        heading: "9.4",
        text: "Termination does not affect any rights or obligations which by their nature should survive, including limitations of liability, disclaimers, and intellectual property provisions.",
      },
    ],
  },
  {
    title: "10. Disclaimers and limitation of liability",
    clauses: [
      {
        heading: "10.1",
        text: '"As is" basis. The Service is provided on an "as is" and "as available" basis. To the fullest extent permitted by law, we disclaim all warranties, whether express, implied, statutory, or otherwise, including any implied warranties of:',
        bullets: [
          "merchantability;",
          "fitness for a particular purpose;",
          "non-infringement; and",
          "that the Service will be uninterrupted, error-free, secure, or free from harmful components.",
        ],
      },
      {
        heading: "10.2",
        text: "No guarantees. We do not guarantee that:",
        bullets: [
          "the Service will meet your requirements or achieve any particular result;",
          "any defects or errors will be corrected;",
          "Your Data will not be lost or corrupted; or",
          "third-party services or integrations used with the Service will remain available or operate without issues.",
        ],
      },
      {
        heading: "10.3",
        text: "Indirect and consequential loss. To the fullest extent permitted by law, we are not liable for any:",
        bullets: [
          "indirect, incidental, special, punitive, or consequential damages;",
          "loss of profits, revenue, business, contracts, savings, goodwill, or data; or",
          "business interruption or reputational damage, arising out of or in connection with your use of (or inability to use) the Service, even if we have been advised of the possibility of such damages.",
        ],
      },
      {
        heading: "10.4",
        text: "Cap on liability. To the fullest extent permitted by law, our total aggregate liability arising out of or in connection with these Terms or the Service (whether in contract, tort, negligence, strict liability, or otherwise) is limited to the amount you have paid to us for the Service in the twelve (12) months immediately preceding the event giving rise to the claim (or, if you have not paid any fees, £0).",
      },
      {
        heading: "10.5",
        text: "Exceptions. Nothing in these Terms excludes or limits liability that cannot be excluded or limited under applicable law, such as liability for:",
        bullets: [
          "death or personal injury caused by negligence;",
          "fraud or fraudulent misrepresentation; or",
          "any other liability which cannot be excluded under applicable law.",
        ],
      },
    ],
  },
  {
    title: "11. Changes to the service and these terms",
    clauses: [
      {
        heading: "11.1",
        text: "We may update, modify, or discontinue parts of the Service at any time. Where a change is material and negatively affects you, we will take reasonable steps to notify you in advance, where practicable.",
      },
      {
        heading: "11.2",
        text: "We may update these Terms from time to time. If we make material changes, we will notify you via the Service or by email.",
      },
      {
        heading: "11.3",
        text: "The updated Terms will be effective from the date indicated. If you continue to use the Service after the updated Terms take effect, you are deemed to have accepted them. If you do not agree, you must stop using the Service.",
      },
    ],
  },
  {
    title: "12. Contact",
    clauses: [
      {
        heading: "12.1",
        text: "If you have any questions about these Terms or the Service, you can contact us at support@invoiceu.co.uk.",
      },
    ],
  },
];

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <header className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Terms &amp; Conditions
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">
              InvoiceU service terms
            </h1>
            <p className="text-xs text-slate-500">
              Version {TERMS_VERSION} · Last updated {TERMS_LAST_UPDATED}
            </p>
          </div>
          <a
            href="/"
            className="self-start text-xs font-semibold text-slate-700 underline hover:text-slate-900"
          >
            Back to app
          </a>
        </header>

        <div className="grid gap-4">
          {sections.map((section) => (
            <section
              key={section.title}
              className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
            >
              <h2 className="text-sm font-semibold text-slate-900">
                {section.title}
              </h2>
              <div className="mt-2 space-y-3">
                {section.clauses.map((clause) => (
                  <div
                    key={`${section.title}-${clause.heading}`}
                    className="text-sm leading-relaxed text-slate-700"
                  >
                    <p>
                      <span className="font-semibold text-slate-900">
                        {clause.heading}{" "}
                      </span>
                      {clause.text}
                    </p>
                    {clause.bullets && (
                      <ul className="mt-2 list-disc space-y-1 pl-5">
                        {clause.bullets.map((bullet) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                    )}
                    {clause.note && (
                      <p className="mt-2 text-sm leading-relaxed text-slate-700">
                        {clause.note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
